import { Component } from '@verndale/core';
import { isLargeDesktop, debounce } from '../../helpers';
import Accordion from '../accordion';
import { s as setCookie } from '../cookie-util';

class Header extends Component {
  lastScrollTop: number;
  isLargeDesktop: boolean;
  bodyScrollTop: number;
  selectedNavCookieName: string;

  private boundOpenLevel2Menu: (event: Event) => void;
  private boundOpenLevel3Menu: (event: Event) => void;
  private boundCloseLevel3Menus: (event: Event) => void;
  private boundOpenMobileNavigation: (event: Event) => void;
  private boundCloseMobileNavigation: (event: Event) => void;
  private boundCloseLevel3Menu: (event: Event) => void;

  private toggleMenuVisibility(
    trigger: HTMLElement,
    isExpanded: boolean,
    menuId?: string | null
  ): HTMLElement | null {
    trigger.setAttribute('aria-expanded', isExpanded.toString());
    if (!menuId) return null;
    const menu = document.getElementById(menuId) as HTMLElement;
    if (menu) menu.setAttribute('aria-hidden', (!isExpanded).toString());
    if (!isLargeDesktop() && menu)
      requestAnimationFrame(() => {
        if (isExpanded) menu.classList.add('active');
        else menu.classList.remove('active');
      });
    return menu;
  }

  constructor(el: HTMLElement) {
    super(el);
    this.lastScrollTop = 0;
    this.isLargeDesktop = isLargeDesktop();
    this.bodyScrollTop = 0;
    this.selectedNavCookieName = 'CACUWEB.selectedNav';

    this.boundOpenLevel2Menu = this.openLevel2Menu.bind(this);
    this.boundOpenLevel3Menu = this.openLevel3Menu.bind(this);
    this.boundCloseLevel3Menus = this.closeLevel3Menus.bind(this);
    this.boundOpenMobileNavigation = this.openMobileNavigation.bind(this);
    this.boundCloseMobileNavigation = this.closeMobileNavigation.bind(this);
    this.boundCloseLevel3Menu = this.closeLevel3Menu.bind(this);
    this.addBoundedListeners();
  }

  setupDefaults() {
    this.dom = {
      el: this.el,
      hamburguerButton: this.el.querySelector<HTMLElement>('.navigation-menu__hamburguer-button'),
      closeButton: this.el.querySelector<HTMLElement>('.navigation-menu__close-button'),

      //--- LEVEL 1 MENU
      level1Menu: this.el.querySelector<HTMLElement>('.navigation-menu__level-1-menu'),

      //--- LEVEL 2 MENU
      level2Triggers: this.el.querySelectorAll<HTMLElement>(
        '.navigation-menu__level-2-menu-trigger'
      ),
      level2Menus: this.el.querySelectorAll<HTMLElement>('.navigation-menu__level-2-menu'),

      //--- LEVEL 3 MENU
      level3Triggers: this.el.querySelectorAll<HTMLElement>(
        '.navigation-menu__level-3-menu-trigger'
      ),
      level3Menus: this.el.querySelectorAll<HTMLElement>('.navigation-menu__level-3-menu'),
      level3MenuItems: this.el.querySelectorAll<HTMLElement>('.navigation-menu__level-3-menu-item'),
      level3BackButtons: this.el.querySelectorAll<HTMLElement>(
        '.navigation-menu__level-3-menu-back-button'
      ),
      level3Accordions: this.el.querySelectorAll<HTMLElement>('.accordion')
    };

    if (!this.isLargeDesktop) {
      Array.from(this.dom.level3Accordions as NodeListOf<HTMLElement>).forEach(accordion => {
        new Accordion(accordion);
      });
    }
  }

  addBoundedListeners() {
    (this.dom.level2Triggers as NodeListOf<HTMLElement>).forEach((level2Trigger, index) => {
      level2Trigger.addEventListener('click', this.boundOpenLevel2Menu);
      level2Trigger.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter') this.boundOpenLevel2Menu(e);
        if (e.key === 'Escape') this.closeAllMenus();

        // Handle left/right navigation for level 2 items
        if (e.key === 'ArrowRight') {
          const nextTrigger = (this.dom.level2Triggers as NodeListOf<HTMLElement>)[index + 1];
          if (nextTrigger) {
            e.preventDefault();
            (nextTrigger as HTMLElement).focus();
          }
        }
        if (e.key === 'ArrowLeft') {
          const prevTrigger = (this.dom.level2Triggers as NodeListOf<HTMLElement>)[index - 1];
          if (prevTrigger) {
            e.preventDefault();
            (prevTrigger as HTMLElement).focus();
          }
        }
      });
    });

    if (this.isLargeDesktop) {
      (this.dom.level1Menu as HTMLElement).addEventListener(
        'mouseleave',
        this.boundCloseLevel3Menus
      );
      Array.from(this.dom.level3Triggers as NodeListOf<HTMLElement>).forEach(
        (level3Trigger, index) => {
          level3Trigger.addEventListener('mouseenter', this.boundOpenLevel3Menu);
          level3Trigger.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter') this.boundOpenLevel3Menu(e);
            if (e.key === 'Escape') this.closeLevel3Menus();

            // Handle left/right navigation for level 3 items
            if (e.key === 'ArrowRight') {
              const nextTrigger = (this.dom.level3Triggers as NodeListOf<HTMLElement>)[index + 1];
              if (nextTrigger) {
                e.preventDefault();
                (nextTrigger as HTMLElement).focus();
              }
            }
            if (e.key === 'ArrowLeft') {
              const prevTrigger = (this.dom.level3Triggers as NodeListOf<HTMLElement>)[index - 1];
              if (prevTrigger) {
                e.preventDefault();
                (prevTrigger as HTMLElement).focus();
              }
            }
          });
        }
      );
    }

    if (!this.isLargeDesktop) {
      (this.dom.hamburguerButton as HTMLElement).addEventListener(
        'click',
        this.boundOpenMobileNavigation
      );

      (this.dom.closeButton as HTMLElement).addEventListener(
        'click',
        this.boundCloseMobileNavigation
      );

      Array.from(this.dom.level3Triggers as NodeListOf<HTMLElement>).forEach(level3Trigger => {
        level3Trigger.addEventListener('click', this.boundOpenLevel3Menu);
      });

      Array.from(this.dom.level3BackButtons as NodeListOf<HTMLElement>).forEach(
        level3BackButton => {
          level3BackButton.addEventListener('click', this.boundCloseLevel3Menu);
        }
      );
    }

    window.addEventListener('resize', debounce(this.checkViewportWidth.bind(this), 300));
  }

  removeListeners() {
    (this.dom.level2Triggers as NodeListOf<HTMLElement>).forEach(level2Trigger => {
      level2Trigger.removeEventListener('click', this.boundOpenLevel2Menu);
      level2Trigger.removeEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter') this.boundOpenLevel2Menu(e);
        if (e.key === 'Escape') this.closeAllMenus();
      });
    });

    if (!this.isLargeDesktop) {
      (this.dom.level1Menu as HTMLElement).removeEventListener(
        'mouseleave',
        this.boundCloseLevel3Menus
      );
      Array.from(this.dom.level3Triggers as NodeListOf<HTMLElement>).forEach(level3Trigger => {
        level3Trigger.removeEventListener('mouseenter', this.boundOpenLevel3Menu);
        level3Trigger.removeEventListener('keydown', (e: KeyboardEvent) => {
          if (e.key === 'Enter') this.boundOpenLevel3Menu(e);
          if (e.key === 'Escape') this.closeLevel3Menus();
        });
      });
    }

    if (this.isLargeDesktop) {
      (this.dom.hamburguerButton as HTMLElement).removeEventListener(
        'click',
        this.boundOpenMobileNavigation
      );

      (this.dom.closeButton as HTMLElement).removeEventListener(
        'click',
        this.boundCloseMobileNavigation
      );

      Array.from(this.dom.level3Triggers as NodeListOf<HTMLElement>).forEach(level3Trigger => {
        level3Trigger.removeEventListener('click', this.boundOpenLevel3Menu);
      });

      Array.from(this.dom.level3BackButtons as NodeListOf<HTMLElement>).forEach(
        level3BackButton => {
          level3BackButton.removeEventListener('click', this.boundCloseLevel3Menu);
        }
      );
    }
  }

  closeLevel2Menus() {
    (this.dom.level2Triggers as NodeListOf<HTMLElement>).forEach(trigger => {
      this.toggleMenuVisibility(trigger, false, trigger.getAttribute('aria-controls'));
    });
  }

  closeLevel3Menus() {
    Array.from(this.dom.level3Triggers as NodeListOf<HTMLElement>).forEach(button => {
      this.toggleMenuVisibility(button, false, button.getAttribute('aria-controls'));
    });
  }

  closeLevel3Menu(event: Event) {
    const button = event.currentTarget as HTMLElement;
    const menu = button.closest('.navigation-menu__level-3-menu') as HTMLElement;
    menu.classList.remove('active');
    button.blur();
    menu.ontransitionend = () => {
      button.setAttribute('aria-expanded', 'false');
      menu.setAttribute('aria-hidden', 'true');
      menu.ontransitionend = null;
    };
  }

  closeAllMenus() {
    this.closeLevel2Menus();
    this.closeLevel3Menus();
  }

  openLevel2Menu(event: Event) {
    const currentTarget = event?.currentTarget as HTMLElement;
    const navCookieName = currentTarget?.getAttribute('data-nav-cookie-name');
    if (navCookieName) {
      setCookie(this.selectedNavCookieName, navCookieName, 1);
    }

    this.closeAllMenus();
    const button = event.currentTarget as HTMLElement;
    this.toggleMenuVisibility(button, true, button.getAttribute('aria-controls'));
  }

  openLevel3Menu(event: Event) {
    this.closeLevel3Menus();
    const button = event.currentTarget as HTMLElement;
    this.toggleMenuVisibility(button, true, button.getAttribute('aria-controls'));
  }

  openMobileNavigation() {
    this.lockScroll();
    this.el.classList.add('header--navigation-opened');
    (this.dom.level1Menu as HTMLElement).setAttribute('aria-hidden', 'false');
    (this.dom.closeButton as HTMLElement).style.display = 'block';
  }

  closeMobileNavigation() {
    this.el.classList.remove('header--navigation-opened');
    this.unlockScroll();
    this.closeLevel3Menus();
    (this.dom.closeButton as HTMLElement).style.display = 'none';
  }

  lockScroll() {
    setTimeout(() => {
      if (!this.isLargeDesktop) {
        this.bodyScrollTop = window.scrollY || document.documentElement.scrollTop;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.bodyScrollTop}px`;
        document.body.style.width = '100%';
      }
    }, 300);
  }

  unlockScroll() {
    if (!this.isLargeDesktop) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo({ top: this.bodyScrollTop, left: 0, behavior: 'instant' });
    }
  }

  checkViewportWidth() {
    if (this.isLargeDesktop !== isLargeDesktop()) {
      this.closeMobileNavigation();
      this.isLargeDesktop = isLargeDesktop();
      this.removeListeners();
      this.addBoundedListeners();
    }
  }
}

export default Header;
